import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatarCnpj, ROTULO_TRATAMENTO, type FiltroProduto, type StatusRevisao, type TipoProduto, type Tratamento } from '@via-tax/contracts'
import { Aviso, Botao, Entrada, EstadoVazio, Selecao, Selo } from '@via-tax/ui'
import { useState } from 'react'
import { dataBr, reais } from '../formatos'
import type { ContextoSessao } from '../tipos'
import { mensagemDeErro, useTRPC } from '../trpc'
import { DetalheProduto } from './DetalheProduto'

/**
 * Revisão de Estoque (arquitetura §7.5): fila ordenada por faturamento, com o %
 * acumulado da receita — revisar 40 produtos que cobrem 85% das vendas em vez de
 * 2.000 em ordem alfabética.
 */

const POR_PAGINA = 50

export const ROTULO_STATUS: Record<StatusRevisao, string> = {
  PENDENTE: 'Sem sugestão',
  SUGERIDO: 'A revisar',
  APROVADO: 'Aprovado',
  REVISAR: 'Revisar de novo',
}
const TOM_STATUS: Record<StatusRevisao, 'neutro' | 'ok' | 'alerta' | 'navy'> = { PENDENTE: 'alerta', SUGERIDO: 'navy', APROVADO: 'ok', REVISAR: 'alerta' }

const ALERTAS: Array<{ v: NonNullable<FiltroProduto['alerta']>; l: string }> = [
  { v: 'pendente', l: 'Não aprovados' },
  { v: 'revisar', l: 'Mudaram depois de aprovados' },
  { v: 'confianca_baixa', l: 'Confiança baixa' },
  { v: 'com_is', l: 'Com Imposto Seletivo' },
  { v: 'ncm_invalido', l: 'NCM inexistente' },
  { v: 'sem_ncm', l: 'Sem NCM' },
]

export function Confianca({ valor }: { valor: number }) {
  const tom = valor >= 0.85 ? 'ok' : valor >= 0.6 ? 'neutro' : 'alerta'
  return (
    <Selo tom={tom}>
      <span className="num">{Math.round(valor * 100)}%</span>
    </Selo>
  )
}

export function RevisaoEstoque({ tipo, contexto }: { tipo: TipoProduto; contexto: ContextoSessao }) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const [filtro, setFiltro] = useState<Pick<FiltroProduto, 'status' | 'alerta' | 'ncm' | 'busca'>>({})
  const [busca, setBusca] = useState('')
  const [ncm, setNcm] = useState('')
  const [pagina, setPagina] = useState(1)
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set())
  const [aberto, setAberto] = useState<string | null>(null)
  const [confirmarMassa, setConfirmarMassa] = useState(false)
  const podeAprovar = (contexto.capacidades as string[]).includes('aprovar_revisao_produto')

  const resumo = useQuery(trpc.produto.resumo.queryOptions())
  const lista = useQuery({
    ...trpc.produto.listar.queryOptions({ tipo, ...filtro, pagina, porPagina: POR_PAGINA }),
    placeholderData: keepPreviousData,
  })
  const atualizar = () => {
    setSelecionados(new Set())
    return qc.invalidateQueries({ queryKey: trpc.produto.pathKey() })
  }
  const aceitar = useMutation(trpc.produto.aceitarSugestoes.mutationOptions({ onSuccess: atualizar }))
  const emMassa = useMutation(
    trpc.produto.aplicarEmMassa.mutationOptions({
      onSuccess: () => {
        setConfirmarMassa(false)
        return atualizar()
      },
    }),
  )
  const reprocessar = useMutation(trpc.produto.reprocessar.mutationOptions())

  const mudar = (p: Partial<typeof filtro>) => {
    setFiltro((f) => ({ ...f, ...p }))
    setPagina(1)
    setSelecionados(new Set())
    setConfirmarMassa(false)
  }
  const d = lista.data
  const r = resumo.data?.porTipo.find((x) => x.tipo === tipo)
  const paginas = d ? Math.max(1, Math.ceil(d.total / POR_PAGINA)) : 1
  const titulo = tipo === 'SAIDA' ? 'Produtos de saída' : 'Insumos de entrada'
  const semFiltro = !Object.values(filtro).some(Boolean)
  const alternar = (id: string) =>
    setSelecionados((s) => {
      const n = new Set(s)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{titulo}</h1>
          <p className="mt-1 max-w-2xl text-sm text-tinta-2">
            {tipo === 'SAIDA'
              ? 'Como cada produto vendido será tributado em 2027: cClassTrib, CST e tratamento pela LC 214/2025.'
              : 'O que cada compra representa para a empresa: destinação e direito a crédito de IBS/CBS.'}{' '}
            Ordenado pelo que mais pesa no faturamento.
          </p>
        </div>
        <div className="flex gap-2">
          {podeAprovar && (
            <Botao variante="fantasma" disabled={reprocessar.isPending} onClick={() => reprocessar.mutate()} title="Refaz o catálogo a partir das notas e reclassifica o que mudou">
              {reprocessar.isSuccess ? 'Reclassificação agendada' : 'Reclassificar'}
            </Botao>
          )}
          <a href={`/produtos/de-para?tipo=${tipo}&formato=csv`}>
            <Botao>Exportar de-para (CSV)</Botao>
          </a>
        </div>
      </header>

      {r && r.produtos > 0 && (
        <section className="grid grid-cols-2 gap-3 rounded-lg border border-linha bg-painel p-4 sm:grid-cols-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.06em] text-tinta-3">Produtos</p>
            <p className="num text-lg font-semibold">{r.produtos.toLocaleString('pt-BR')}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.06em] text-tinta-3">Aprovados</p>
            <p className="num text-lg font-semibold">{(r.porStatus.APROVADO ?? 0).toLocaleString('pt-BR')}</p>
          </div>
          <div className="col-span-2">
            <p className="text-[11px] uppercase tracking-[0.06em] text-tinta-3">{tipo === 'SAIDA' ? 'Receita' : 'Compras'} com perfil aprovado</p>
            <div className="mt-1.5 flex items-center gap-3">
              <div className="h-2 flex-1 overflow-hidden rounded bg-fundo">
                <div className="h-full bg-ok" style={{ width: `${Math.round(r.coberturaAprovada * 100)}%` }} />
              </div>
              <span className="num text-sm font-semibold">{Math.round(r.coberturaAprovada * 100)}%</span>
            </div>
            <p className="mt-1 text-[11px] text-tinta-3">
              <span className="num">{reais(r.valorAprovado)}</span> de <span className="num">{reais(r.valorTotal)}</span> · base de conhecimento{' '}
              <span className="num">{resumo.data?.versaoKb}</span>
            </p>
          </div>
        </section>
      )}

      <form
        className="grid grid-cols-2 gap-2 rounded-lg border border-linha bg-painel p-3 lg:grid-cols-5"
        onSubmit={(e) => {
          e.preventDefault()
          mudar({ busca: busca.trim() || undefined, ncm: /^\d{2,8}$/.test(ncm) ? ncm : undefined })
        }}
      >
        <Entrada className="col-span-2" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder={tipo === 'SAIDA' ? 'Código, GTIN ou descrição' : 'Código, descrição ou fornecedor'} />
        <Entrada value={ncm} onChange={(e) => setNcm(e.target.value.replace(/\D/g, '').slice(0, 8))} placeholder="NCM (início)" inputMode="numeric" />
        <Selecao value={filtro.status ?? ''} onChange={(e) => mudar({ status: (e.target.value || undefined) as StatusRevisao | undefined })} aria-label="Situação">
          <option value="">Qualquer situação</option>
          {Object.entries(ROTULO_STATUS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </Selecao>
        <Selecao value={filtro.alerta ?? ''} onChange={(e) => mudar({ alerta: (e.target.value || undefined) as FiltroProduto['alerta'] })} aria-label="Alerta">
          <option value="">Sem filtro de alerta</option>
          {ALERTAS.map((a) => (
            <option key={a.v} value={a.v}>
              {a.l}
            </option>
          ))}
        </Selecao>
        <button type="submit" className="hidden" />
      </form>

      {lista.isError && <Aviso>{mensagemDeErro(lista.error)}</Aviso>}
      {(aceitar.isError || emMassa.isError) && <Aviso>{mensagemDeErro(aceitar.error ?? emMassa.error)}</Aviso>}
      {aceitar.isSuccess && (
        <Aviso tom="ok">
          {aceitar.data.aprovados} perfil(is) aprovado(s){aceitar.data.semSugestao ? ` · ${aceitar.data.semSugestao} sem sugestão ficaram de fora` : ''}.
        </Aviso>
      )}
      {emMassa.isSuccess && <Aviso tom="ok">{emMassa.data.aprovados} perfil(is) aprovado(s) em massa.</Aviso>}

      {podeAprovar && d && d.total > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Botao
            variante="primario"
            disabled={!selecionados.size || aceitar.isPending}
            onClick={() => aceitar.mutate({ produtoIds: [...selecionados] })}
          >
            Aceitar sugestão{selecionados.size ? ` de ${selecionados.size}` : ''}
          </Botao>
          {!confirmarMassa ? (
            <Botao variante="fantasma" onClick={() => setConfirmarMassa(true)} disabled={d.total > 5000}>
              Aceitar sugestões de todo o filtro ({d.total.toLocaleString('pt-BR')})
            </Botao>
          ) : (
            <span className="flex flex-wrap items-center gap-2 rounded-md border border-acao/40 bg-acao/5 px-3 py-1.5">
              Aprovar a sugestão de <strong className="num">{d.total.toLocaleString('pt-BR')}</strong> produto(s) de uma vez? Produtos sem sugestão ficam de fora.
              <Botao
                variante="primario"
                disabled={emMassa.isPending}
                onClick={() => emMassa.mutate({ filtro: { tipo, ...filtro }, acao: { tipo: 'aceitar_sugestoes' }, confirmarQtd: d.total })}
              >
                Confirmar
              </Botao>
              <Botao variante="fantasma" onClick={() => setConfirmarMassa(false)}>
                Cancelar
              </Botao>
            </span>
          )}
        </div>
      )}

      {d && d.total === 0 ? (
        semFiltro ? (
          <EstadoVazio titulo="Catálogo vazio">
            O catálogo é montado a partir das notas enviadas ({tipo === 'SAIDA' ? 'NF-e e NFC-e de saída' : 'NF-e de entrada'}). Depois de
            enviar os XMLs, ele aparece aqui em alguns minutos — ou use “Reclassificar”.
          </EstadoVazio>
        ) : (
          <EstadoVazio titulo="Nenhum produto com esses filtros">Ajuste os filtros acima.</EstadoVazio>
        )
      ) : (
        <div className="overflow-x-auto rounded-lg border border-linha bg-painel">
          <table className="w-full min-w-[940px] text-left text-[13px]">
            <thead className="border-b border-linha text-[11px] uppercase tracking-[0.06em] text-tinta-3">
              <tr>
                {podeAprovar && (
                  <th className="w-8 px-3 py-2.5">
                    <input
                      type="checkbox"
                      aria-label="Selecionar todos da página"
                      checked={Boolean(d?.itens.length) && d!.itens.every((i) => selecionados.has(i.id))}
                      onChange={(e) => setSelecionados(e.target.checked ? new Set(d?.itens.map((i) => i.id)) : new Set())}
                    />
                  </th>
                )}
                <th className="px-3 py-2.5 font-medium">Produto</th>
                <th className="px-3 py-2.5 font-medium">NCM</th>
                <th className="px-3 py-2.5 text-right font-medium">{tipo === 'SAIDA' ? 'Vendas' : 'Compras'}</th>
                <th className="px-3 py-2.5 text-right font-medium" title="Participação acumulada no total, na ordem da fila">
                  Acum.
                </th>
                <th className="px-3 py-2.5 font-medium">Sugestão / perfil</th>
                <th className="px-3 py-2.5 font-medium">Situação</th>
              </tr>
            </thead>
            <tbody className={`divide-y divide-linha ${lista.isFetching ? 'opacity-60' : ''}`}>
              {d?.itens.map((p) => {
                const vigente = p.perfil ?? (p.sugestao?.cClassTrib ? { cClassTrib: p.sugestao.cClassTrib, tratamento: p.sugestao.tratamento } : null)
                return (
                  <tr key={p.id} className="cursor-pointer hover:bg-fundo/60" onClick={() => setAberto(p.id)}>
                    {podeAprovar && (
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" aria-label={`Selecionar ${p.codigoProduto}`} checked={selecionados.has(p.id)} onChange={() => alternar(p.id)} />
                      </td>
                    )}
                    <td className="max-w-[340px] px-3 py-2">
                      <span className="block truncate font-medium">{p.descricao}</span>
                      <span className="block truncate text-[11px] text-tinta-3">
                        <span className="num">{p.codigoProduto}</span>
                        {tipo === 'ENTRADA' && (p.fornecedorNome || p.fornecedorDoc) && (
                          <> · {p.fornecedorNome ?? (p.fornecedorDoc.length === 14 ? formatarCnpj(p.fornecedorDoc) : 'Produtor pessoa física')}</>
                        )}
                      </span>
                    </td>
                    <td className="num px-3 py-2 whitespace-nowrap">
                      {p.ncm ?? <Selo tom="alerta">sem NCM</Selo>}
                      {p.qtdNcmDistintos > 1 && (
                        <span className="ml-1" title="O mesmo código saiu com NCMs diferentes nas notas">
                          <Selo tom="alerta">{p.qtdNcmDistintos} NCMs</Selo>
                        </span>
                      )}
                    </td>
                    <td className="num px-3 py-2 text-right whitespace-nowrap">{reais(p.valor)}</td>
                    <td className="num px-3 py-2 text-right text-tinta-2 whitespace-nowrap">{p.acumuladoPct === null ? '—' : `${Math.round(p.acumuladoPct * 100)}%`}</td>
                    <td className="px-3 py-2">
                      {vigente?.cClassTrib ? (
                        <span className="flex flex-wrap items-center gap-1.5">
                          <span className="num font-medium">{vigente.cClassTrib}</span>
                          <span className="text-tinta-2">{ROTULO_TRATAMENTO[vigente.tratamento as Tratamento] ?? vigente.tratamento}</span>
                          {!p.perfil && p.sugestao && <Confianca valor={p.sugestao.confianca} />}
                          {p.sugestao?.isIncide && <Selo tom="alerta">IS</Selo>}
                          {!p.perfil && p.sugestao && p.sugestao.qtdAlertas > 0 && (
                            <span className="text-[11px] text-tinta-3">{p.sugestao.qtdAlertas} alerta(s)</span>
                          )}
                        </span>
                      ) : (
                        <span className="text-tinta-3">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Selo tom={TOM_STATUS[p.status]}>{ROTULO_STATUS[p.status]}</Selo>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {d && d.total > POR_PAGINA && (
            <div className="flex items-center justify-between border-t border-linha px-4 py-2.5 text-xs text-tinta-2">
              <span className="num">
                Página {pagina} de {paginas} · {d.total.toLocaleString('pt-BR')} produtos
              </span>
              <div className="flex gap-2">
                <Botao variante="fantasma" disabled={pagina <= 1} onClick={() => setPagina((x) => x - 1)}>
                  Anterior
                </Botao>
                <Botao variante="fantasma" disabled={pagina >= paginas} onClick={() => setPagina((x) => x + 1)}>
                  Próxima
                </Botao>
              </div>
            </div>
          )}
        </div>
      )}

      {resumo.data?.atualizadoEm && <p className="text-[11px] text-tinta-3">Catálogo atualizado em {dataBr(resumo.data.atualizadoEm)}.</p>}

      {aberto && <DetalheProduto produtoId={aberto} podeAprovar={podeAprovar} fechar={() => setAberto(null)} aoAprovar={atualizar} />}
    </div>
  )
}
